import type { NodeType, RewardDefinition, RewardId } from "./types";

export const SAVE_KEY_V1 = "football-roguelike-prototype-v1";
export const SAVE_KEY_V2 = "football-roguelike-save-v2";
export const TOTAL_SEASONS = 5;
export const WORLD_CUP_OVR_THRESHOLD = 85;
export const WORLD_CUP_REPUTATION_THRESHOLD = 75;
export const RENEWAL_OVR_REPUTATION_THRESHOLD = 70;
export const ROUTE_NODE_COUNTS = [2, 3, 4, 3, 4, 3, 2] as const;
export const NODES_PER_SEASON = ROUTE_NODE_COUNTS.length;
/** Ruta específica del Mundial: grupos, recuperación, dos eliminatorias y descanso antes del boss. */
export const WORLD_CUP_ROUTE_NODE_COUNTS = [2, 3, 4, 3, 2] as const;

// No encounter is completely solved (or lost) on the preparation screen.
// A strong build still matters enormously, but every roll keeps a small upset chance.
export const ENCOUNTER_WIN_CHANCE_FLOOR = 0.12;
export const ENCOUNTER_WIN_CHANCE_CEILING = 0.88;

// Each stage has its own onboarding, challenge and recovery target. This is the
// single source of truth for the five-step career difficulty curve.
export const STAGE_BOSS_BALANCE = [
  { expectedOvr: 60, bossDifficulty: 55, bossReactionDifficulty: 58, requiredWins: 1, intermissionPhysicalRecovery: 0 },
  { expectedOvr: 67, bossDifficulty: 66, bossReactionDifficulty: 69, requiredWins: 1, intermissionPhysicalRecovery: 16 },
  { expectedOvr: 74, bossDifficulty: 73, bossReactionDifficulty: 76, requiredWins: 2, intermissionPhysicalRecovery: 14 },
  { expectedOvr: 81, bossDifficulty: 78, bossReactionDifficulty: 85, requiredWins: 3, intermissionPhysicalRecovery: 12 },
  { expectedOvr: 88, bossDifficulty: 85, bossReactionDifficulty: 88, requiredWins: 3, intermissionPhysicalRecovery: 10 },
] as const;

export function getStageBossBalance(stage: number) {
  const index = Math.min(Math.max(Math.floor(stage) - 1, 0), STAGE_BOSS_BALANCE.length - 1);
  return STAGE_BOSS_BALANCE[index]!;
}

// A late boss reaction remains a pressure moment; this keeps its resource
// choice meaningful even where the base boss curve is intentionally forgiving.
export const BOSS_REACTION_ROUND_DIFFICULTY = 1;
export const TEAMMATES = ["Leo Santos", "Bruno Costa", "Iker Molina", "Nico Rivas", "Dani Romero", "Marco Vidal", "Joel Castro", "Hugo Serra", "Eric Pons", "Pablo Cano", "Sergio Luna", "Adrián Gil"] as const;
export const OPPONENTS = ["Tomás Silva", "Martín Duarte", "Enzo Méndez", "Gael Torres", "Lucas Pereira", "Iván Acosta", "Mateo Rossi", "Thiago León", "Franco Suárez", "Diego Lemos", "Julián Soto", "Alan Ferreyra"] as const;

export const NODE_TYPES = {
  match: { name: "Tramo de liga", icon: "mdi:soccer", description: "Cinco partidos (3/1/0): 8 puntos superan el tramo. Ganas +1 OVR al jugar, +2 con 150′ y dos contribuciones (gol o asistencia), o +3 con 300′, cuatro contribuciones y 12 puntos obtenidos en partidos disputados." },
  rest: { name: "Descanso", icon: "mdi:grass", description: "Recupera físico y moral." },
  event: { name: "Evento", icon: "mdi:help", description: "Una decisión con consecuencias." },
  scout: { name: "Ojeador", icon: "mdi:binoculars", description: "Consigue un consumible o una reliquia para especializar tu build." },
  derby: { name: "Derbi", icon: "mdi:fire", description: "Ganar suma 3 OVR, recupera físico y eleva la moral y el prestigio en el club." },
} as const;

export const REWARDS: Record<RewardId, RewardDefinition> = {
  "remate-colocado": {
    id: "remate-colocado", name: "Remate colocado", kind: "card", kindLabel: "Consumible", family: "Técnica",
    art: "reward-remate-colocado", description: "+3 OVR efectivo al calcular tu opción de ganar el próximo encuentro.",
    effect: { type: "rating", amount: 3 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "pase-entre-lineas": {
    id: "pase-entre-lineas", name: "Pase entre líneas", kind: "card", kindLabel: "Consumible", family: "Técnica",
    art: "reward-pase-entre-lineas", description: "+4 puntos porcentuales a la opción de registrar cada asistencia del próximo encuentro.",
    effect: { type: "assistChance", percentagePoints: 4 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "presion-coordinada": {
    id: "presion-coordinada", name: "Presión coordinada", kind: "card", kindLabel: "Consumible", family: "Técnica",
    art: "reward-presion-coordinada", description: "+3 OVR efectivo al calcular tu opción de ganar el próximo encuentro.",
    effect: { type: "rating", amount: 3 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "botas-ligeras": {
    id: "botas-ligeras", name: "Botas ligeras", kind: "relic", kindLabel: "Reliquia", family: "Resistencia", stackLimit: 3,
    art: "reward-botas-ligeras", description: "Reduce en 2 el coste físico de todos los encuentros por cada copia.",
    effect: { type: "physicalCostReduction", amount: 2 }, allowedEncounters: ["match", "derby", "boss"],
  },
  brazalete: {
    id: "brazalete", name: "Brazalete", kind: "relic", kindLabel: "Reliquia", family: "Vestuario", stackLimit: 3,
    art: "reward-brazalete", description: "+4 moral tras cualquier encuentro por copia. Con 75 o más de moral, aporta +1 OVR efectivo por copia, hasta +2.",
    effect: { type: "moraleBonus", amount: 4, ratingAmount: 1, threshold: 75, maximumRatingBonus: 2 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "balon-parado": {
    id: "balon-parado", name: "Balón parado", kind: "card", kindLabel: "Consumible", family: "Técnica",
    art: "reward-balon-parado", description: "+5 OVR efectivo al calcular tu opción de ganar la próxima ronda decisiva.",
    effect: { type: "bossRating", amount: 5 }, allowedEncounters: ["boss"],
  },
  "gel-energetico": {
    id: "gel-energetico", name: "Gel energético", kind: "card", kindLabel: "Consumible", family: "Resistencia",
    art: "reward-gel-energetico", description: "+20 físico antes de pagar el coste del próximo encuentro; puede reactivar Pulsera biométrica.",
    effect: { type: "physicalRestore", amount: 20 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "charla-capitan": {
    id: "charla-capitan", name: "Charla del capitán", kind: "card", kindLabel: "Consumible", family: "Vestuario",
    art: "reward-charla-capitan", description: "+15 moral antes de calcular el rendimiento del próximo encuentro; estabiliza una build de vestuario.",
    effect: { type: "moraleRestore", amount: 15 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "vendaje-compresivo": {
    id: "vendaje-compresivo", name: "Vendaje compresivo", kind: "card", kindLabel: "Consumible", family: "Supervivencia",
    art: "reward-vendaje-compresivo", description: "Ignora la penalización por físico bajo durante el próximo encuentro; es el remate consumible de la build de supervivencia.",
    effect: { type: "ignorePhysicalPenalty" }, allowedEncounters: ["match", "derby", "boss"],
  },
  "pulsera-biometrica": {
    id: "pulsera-biometrica", name: "Pulsera biométrica", kind: "relic", kindLabel: "Reliquia", family: "Resistencia", stackLimit: 1,
    art: "reward-pulsera-biometrica", description: "+2 OVR efectivo si llegas al encuentro con 75 o más de físico tras pagar su coste. Combina con Botas ligeras y Gel energético.",
    effect: { type: "highPhysicalRating", amount: 2, threshold: 75 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "segundo-pulmon": {
    id: "segundo-pulmon", name: "Tercer pulmón", kind: "relic", kindLabel: "Reliquia", family: "Supervivencia", stackLimit: 1,
    art: "reward-segundo-pulmon", description: "Una vez por etapa, recupera 18 físico si el coste de un encuentro te dejaría por debajo de 35. Abre la build de supervivencia.",
    effect: { type: "physicalSafety", amount: 18, threshold: 35 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "lider-silencioso": {
    id: "lider-silencioso", name: "Líder silencioso", kind: "relic", kindLabel: "Reliquia", family: "Vestuario", stackLimit: 1,
    art: "reward-lider-silencioso", description: "Cada descanso recupera moral adicional igual al 25% del físico recuperado; Camilla portátil aumenta ambas recuperaciones.",
    effect: { type: "recoveryConversion", percentage: 25 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "camilla-portatil": {
    id: "camilla-portatil", name: "Camilla portátil", kind: "relic", kindLabel: "Reliquia", family: "Recuperación", stackLimit: 2,
    art: "reward-camilla-portatil", description: "+10 físico en los descansos y +5 entre rondas decisivas por copia; Líder silencioso convierte parte del descanso mixto en moral.",
    effect: { type: "restPhysicalBonus", amount: 10 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "plan-personalizado": {
    id: "plan-personalizado", name: "Plan personalizado", kind: "relic", kindLabel: "Reliquia", family: "Recuperación", stackLimit: 1,
    art: "reward-plan-personalizado", description: "Después de descansar, el siguiente encuentro cuesta 8 físico menos.",
    effect: { type: "postRestCostReduction", amount: 8 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "foto-cantera": {
    id: "foto-cantera", name: "Foto de la cantera", kind: "relic", kindLabel: "Reliquia", family: "Supervivencia", stackLimit: 1,
    art: "reward-foto-cantera", description: "Compensa hasta 4 OVR de la penalización por moral baja. Nunca empeora al recuperar moral.",
    effect: { type: "lowMoraleRating", amount: 4, threshold: 30 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "rodilleras-antiguas": {
    id: "rodilleras-antiguas", name: "Rodilleras antiguas", kind: "relic", kindLabel: "Reliquia", family: "Supervivencia", stackLimit: 1,
    art: "reward-rodilleras-antiguas", description: "Con menos de 35 físico, limita la penalización física a −5 OVR. Combina con Foto de la cantera y Vendaje compresivo.",
    effect: { type: "physicalPenaltyCap", amount: 5, threshold: 35 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "partido-a-partido": {
    id: "partido-a-partido", name: "Partido a partido", kind: "relic", kindLabel: "Reliquia", family: "Vestuario", stackLimit: 1,
    art: "reward-partido-a-partido", description: "Con menos de 50 moral, reduce en 10 la pérdida de moral de cada derrota; nunca convierte perder en recuperación.",
    effect: { type: "lowMoraleLossRecovery", amount: 10, threshold: 50 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "recorte-periodico": {
    id: "recorte-periodico", name: "Recorte de periódico", kind: "relic", kindLabel: "Reliquia", family: "Racha", stackLimit: 1,
    art: "reward-recorte-periodico", description: "+1 OVR efectivo por victoria consecutiva, hasta +3. Ritual del vestuario protege el físico al alcanzar la tercera.",
    effect: { type: "streakRating", amount: 1, maximum: 3 }, allowedEncounters: ["match", "derby", "boss"],
  },
  "ritual-vestuario": {
    id: "ritual-vestuario", name: "Ritual del vestuario", kind: "relic", kindLabel: "Reliquia", family: "Racha", stackLimit: 1,
    art: "reward-ritual-vestuario", description: "Desde la tercera victoria consecutiva, cada nueva victoria recupera 5 físico y sostiene el bonus de Recorte de periódico.",
    effect: { type: "streakPhysicalRecovery", amount: 5, streak: 3 }, allowedEncounters: ["match", "derby", "boss"],
  },
};

export const REWARD_IDS = Object.keys(REWARDS) as RewardId[];

export const ENCOUNTER_PHYSICAL_COST: Readonly<Record<"match" | "derby" | "boss", number>> = {
  match: 16,
  derby: 22,
  boss: 18,
};

export const OPPONENT_PROFILES = {
  aggressive: {
    id: "aggressive", title: "Rival agresivo", icon: "mdi:sword-cross",
    summary: "Presiona arriba, adelanta sus líneas y acelera el ritmo desde el inicio.",
  },
  compact: {
    id: "compact", title: "Rival compacto", icon: "mdi:shield-half-full",
    summary: "Defiende en bloque bajo, cierra el centro y concede muy pocos metros.",
  },
  adaptive: {
    id: "adaptive", title: "Rival adaptable", icon: "mdi:strategy",
    summary: "Cambia la altura de la presión y el ritmo según avanza el encuentro.",
  },
} as const;

export const COMMON_NODE_POOL: readonly NodeType[] = ["match", "match", "rest", "event", "scout"];
